import React, { memo } from 'react';
import { Pressable, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import { Stylist } from '@/data/types';
import { colors, radius, shadows, spacing, type, useAppTheme } from '@/theme';
import { Avatar } from '../ui/Avatar';
import { FitText } from '../ui/FitText';
import { RatingStars } from '../ui/RatingStars';
import { Button } from '../ui/Button';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { toggleFavoriteStylist } from '@/store/slices/favoritesSlice';
import { ScaledSheet, useCardScale } from '@/theme/responsive';
import { trText } from '@/i18n/plain';
import { useLanguage } from '@/i18n';

// A stylist with no specialties listed shows their title ("Stylist", "Owner & Master Stylist") rather
// than a placeholder that reads as unfinished; every card still gets its line.
function servicesLabel(specialties: string[], max?: number, title?: string) {
  if (specialties.length === 0) return title?.trim() || trText('Stylist', 'স্টাইলিস্ট');
  const list = max ? specialties.slice(0, max) : specialties;
  return list.join(' · ');
}

interface Props {
  stylist: Stylist;
  variant?: 'compact' | 'list' | 'select';
  selected?: boolean;
  onPress?: () => void;
}

function StylistCardComponent({ stylist, variant = 'compact', selected = false, onPress }: Props) {
  useLanguage(); // redraw when the language changes (trText is read at call time)
  const { colors } = useAppTheme();
  const dispatch = useAppDispatch();
  const isFavorite = useAppSelector((state) => state.favorites.stylistIds.includes(stylist.id));
  // A little bigger on a tablet, laptop or monitor, more the wider the window is.
  const grow = useCardScale();
  const goToProfile = () => router.push({ pathname: '/stylist/[id]', params: { id: stylist.id } });
  const handlePress = onPress ?? goToProfile;

  if (variant === 'select') {
    return (
      <Pressable
        accessibilityRole="radio"
        accessibilityState={{ checked: selected }}
        onPress={handlePress}
        style={[
          styles.selectCard,
          { backgroundColor: colors.surface, borderColor: selected ? colors.accent : colors.border },
          selected && styles.selectCardActive,
        ]}>
        <Avatar uri={stylist.photo} name={stylist.name} size={52} />
        <View style={styles.selectBody}>
          <Text style={[type.titleLg, { color: colors.textPrimary }]} numberOfLines={1}>{stylist.name}</Text>
          <RatingStars rating={stylist.rating} reviewCount={stylist.reviewCount} />
          <Text style={[type.caption, styles.specialty, styles.specialtyLeft, { color: colors.accentDark }]} numberOfLines={1}>{servicesLabel(stylist.specialties, undefined, stylist.title)}</Text>
        </View>
        <RadioDot selected={selected} />
      </Pressable>
    );
  }

  if (variant === 'compact') {
    return (
      <Pressable
        accessibilityRole="button"
        onPress={handlePress}
        style={[styles.compactCard, grow > 1 && { width: Math.round(150 * grow) }, { backgroundColor: colors.surface, borderColor: colors.border }]}>
        <View style={styles.compactPhotoWrap}>
          <Avatar uri={stylist.photo} name={stylist.name} size={Math.round(72 * grow)} />
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            onPress={(e) => {
              e.stopPropagation();
              dispatch(toggleFavoriteStylist(stylist.id));
            }}
            style={[styles.favBtnCompact, { backgroundColor: colors.surface, borderColor: colors.border }]}>
            <Ionicons name={isFavorite ? 'heart' : 'heart-outline'} size={14} color={isFavorite ? colors.error : colors.textPrimary} />
          </Pressable>
        </View>
        <FitText width={Math.round(126 * grow)} base={17} min={12} style={[type.titleLg, { color: colors.textPrimary }]}>{stylist.name}</FitText>
        <RatingStars rating={stylist.rating} reviewCount={stylist.reviewCount} />
        <Text style={[type.caption, styles.specialty, { color: colors.accentDark }]} numberOfLines={1}>{servicesLabel(stylist.specialties, 1, stylist.title)}</Text>
        {stylist.shopName ? <Text style={[type.caption, styles.shop, { color: colors.textTertiary }]} numberOfLines={1}>{stylist.shopName}</Text> : null}
        <Button label={trText('Book', 'বুক করুন')} size="sm" onPress={handlePress} style={styles.bookBtn} />
      </Pressable>
    );
  }

  return (
    <Pressable
      accessibilityRole="button"
      onPress={handlePress}
      style={[styles.listCard, { backgroundColor: colors.surface, borderColor: colors.border }]}>
      <Avatar uri={stylist.photo} name={stylist.name} size={64} />
      <View style={styles.listBody}>
        <Text style={[type.titleLg, { color: colors.textPrimary }]} numberOfLines={1}>{stylist.name}</Text>
        <RatingStars rating={stylist.rating} reviewCount={stylist.reviewCount} />
        <Text style={[type.caption, styles.specialty, styles.specialtyLeft, { color: colors.accentDark }]} numberOfLines={1}>{servicesLabel(stylist.specialties, undefined, stylist.title)}</Text>
        <Text style={[type.caption, styles.nextAvailable, { color: colors.info }]}>
          <Ionicons name="time-outline" size={11} color={colors.info} /> {stylist.nextAvailable || 'Contact shop'}
        </Text>
      </View>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        onPress={(e) => {
          e.stopPropagation();
          dispatch(toggleFavoriteStylist(stylist.id));
        }}
        style={styles.favBtnList}>
        <Ionicons name={isFavorite ? 'heart' : 'heart-outline'} size={18} color={isFavorite ? colors.error : colors.textPrimary} />
      </Pressable>
    </Pressable>
  );
}

export const StylistCard = memo(StylistCardComponent);

function RadioDot({ selected }: { selected: boolean }) {
  const { colors } = useAppTheme();
  return (
    <View style={[styles.radio, { borderColor: selected ? colors.accent : colors.borderStrong }]}>
      {selected && <View style={[styles.radioInner, { backgroundColor: colors.accentFill }]} />}
    </View>
  );
}

const styles = ScaledSheet.create({
  compactCard: {
    width: 150,
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    alignItems: 'center',
    gap: 4,
    ...shadows.card,
  },
  compactPhotoWrap: { position: 'relative', marginBottom: spacing.xs },
  favBtnCompact: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border,
  },
  specialty: { color: colors.accentDark, textAlign: 'center' },
  shop: { color: colors.textTertiary, textAlign: 'center' },
  bookBtn: { marginTop: spacing.xs },
  listCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    ...shadows.card,
  },
  listBody: { flex: 1, gap: 3 },
  nextAvailable: { color: colors.info },
  favBtnList: {
    padding: spacing.xs,
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1.5,
    borderColor: colors.border,
    padding: spacing.lg,
  },
  selectCardActive: { borderColor: colors.accent },
  selectBody: { flex: 1, gap: 3 },
  specialtyLeft: { textAlign: 'left' },
  radio: { width: 22, height: 22, borderRadius: 11, borderWidth: 1.5, borderColor: colors.borderStrong, alignItems: 'center', justifyContent: 'center' },
  radioSelected: { borderColor: colors.accent },
  radioInner: { width: 11, height: 11, borderRadius: 5.5, backgroundColor: colors.accentFill },
});

